import type { OnboardingAnswers, Question } from "../types/onboarding";
import {
	isAdditionalInfoQuestion,
	isFertilityJourneyTypesQuestion,
	isFertilityPathQuestion,
	isGenderRelationshipQuestion,
	isHowDidYouHearQuestion,
	isInterestsQuestion,
	isNeedsMostQuestion,
	isPersonalInfoQuestion,
	isSelectProviderQuestion,
	isSetupProfileDetailsQuestion,
	isSetupProfileQuestion,
} from "../utils/questionTypeGuards";
import * as FormValidationService from "./formValidationService";

/**
 * Validates if a specific question has been completed based on its type
 */
export function validateQuestionCompletion(
	question: Question,
	answers: OnboardingAnswers,
): boolean {
	if (isSetupProfileQuestion(question)) {
		return FormValidationService.hasSetupProfileCompleted(answers);
	}

	if (isSetupProfileDetailsQuestion(question)) {
		return FormValidationService.hasSetupProfileDetailsCompleted(answers);
	}

	if (isPersonalInfoQuestion(question)) {
		return FormValidationService.hasPersonalInfoCompleted(answers);
	}

	if (isGenderRelationshipQuestion(question)) {
		return !!(answers.gender && answers.relationshipStatus);
	}

	if (isFertilityJourneyTypesQuestion(question)) {
		const value = answers[question.field];
		return Array.isArray(value) && value.length > 0;
	}

	if (isNeedsMostQuestion(question)) {
		const value = answers[question.field];
		return Array.isArray(value) && value.length > 0;
	}

	if (isHowDidYouHearQuestion(question)) {
		const value = answers[question.field];
		return Array.isArray(value) && value.length > 0;
	}

	if (isFertilityPathQuestion(question)) {
		const value = answers[question.field];
		return Array.isArray(value) && value.length > 0;
	}

	if (isInterestsQuestion(question)) {
		const value = answers[question.field];
		return Array.isArray(value) && value.length > 0;
	}

	if (isSelectProviderQuestion(question)) {
		const hasClinicProvider = answers.hasClinicProvider;

		// Must have answered the yes/no question
		if (
			hasClinicProvider === null ||
			hasClinicProvider === undefined ||
			hasClinicProvider === ""
		) {
			return false;
		}

		// If answered "yes" (true or "true"), must have provider name
		if (hasClinicProvider === "true" || hasClinicProvider === true) {
			const providerName = answers.providerName;
			return !!(providerName && providerName !== "");
		}

		// If answered "no" (false or "false"), that's valid
		return true;
	}

	if (isAdditionalInfoQuestion(question)) {
		// This screen is optional - users can proceed without providing info
		return true;
	}

	// Default case for other question types
	const value = answers[question.field];
	return !!(value && value !== "");
}

/**
 * Checks if validation should be shown for a specific question type
 */
export function shouldShowValidation(question: Question): boolean {
	return (
		isPersonalInfoQuestion(question) ||
		isSetupProfileQuestion(question) ||
		isSetupProfileDetailsQuestion(question)
	);
}

/**
 * Gets all incomplete fields for a question
 */
export function getIncompleteFields(
	question: Question,
	answers: OnboardingAnswers,
): string[] {
	const incompleteFields: string[] = [];

	if (isSetupProfileQuestion(question)) {
		if (!answers.firstName || answers.firstName.trim() === "") {
			incompleteFields.push("firstName");
		}
		if (!answers.countryCity || answers.countryCity.trim() === "") {
			incompleteFields.push("countryCity");
		}
		return incompleteFields;
	}

	if (isSetupProfileDetailsQuestion(question)) {
		if (!answers.yearOfBirth || answers.yearOfBirth === "") {
			incompleteFields.push("yearOfBirth");
		}
		if (!answers.gender || answers.gender === "") {
			incompleteFields.push("gender");
		}
		return incompleteFields;
	}

	if (isPersonalInfoQuestion(question)) {
		if (!answers.dayOfBirth || !answers.monthOfBirth || !answers.yearOfBirth) {
			incompleteFields.push("dateOfBirth");
		}
		if (
			!answers.languagePreference ||
			answers.languagePreference.trim() === ""
		) {
			incompleteFields.push("languagePreference");
		}
		return incompleteFields;
	}

	if (isGenderRelationshipQuestion(question)) {
		if (!answers.gender) incompleteFields.push("gender");
		if (!answers.relationshipStatus)
			incompleteFields.push("relationshipStatus");
		return incompleteFields;
	}

	if (isFertilityJourneyTypesQuestion(question)) {
		const value = answers[question.field];
		if (!Array.isArray(value) || value.length === 0) {
			incompleteFields.push(question.field);
		}
		return incompleteFields;
	}

	if (isNeedsMostQuestion(question)) {
		const value = answers[question.field];
		if (!Array.isArray(value) || value.length === 0) {
			incompleteFields.push(question.field);
		}
		return incompleteFields;
	}

	if (isHowDidYouHearQuestion(question)) {
		const value = answers[question.field];
		if (!Array.isArray(value) || value.length === 0) {
			incompleteFields.push(question.field);
		}
		return incompleteFields;
	}

	if (isFertilityPathQuestion(question)) {
		const value = answers[question.field];
		if (!Array.isArray(value) || value.length === 0) {
			incompleteFields.push(question.field);
		}
		return incompleteFields;
	}

	if (isInterestsQuestion(question)) {
		const value = answers[question.field];
		if (!Array.isArray(value) || value.length === 0) {
			incompleteFields.push(question.field);
		}
		return incompleteFields;
	}

	if (isSelectProviderQuestion(question)) {
		const hasClinicProvider = answers.hasClinicProvider;

		// Must have answered the yes/no question
		if (
			hasClinicProvider === null ||
			hasClinicProvider === undefined ||
			hasClinicProvider === ""
		) {
			incompleteFields.push("hasClinicProvider");
		}

		// If answered "yes", must have provider name
		if (
			(hasClinicProvider === "true" || hasClinicProvider === true) &&
			(!answers.providerName || answers.providerName === "")
		) {
			incompleteFields.push("providerName");
		}

		return incompleteFields;
	}

	// Default case for other question types
	const value = answers[question.field];
	if (!value || value === "") {
		incompleteFields.push(question.field);
	}

	return incompleteFields;
}

/**
 * Validates multiple questions and returns overall completion status
 */
export function validateMultipleQuestions(
	questions: Question[],
	answers: OnboardingAnswers,
): {
	allCompleted: boolean;
	completedCount: number;
	incompleteQuestions: Question[];
} {
	const incompleteQuestions = questions.filter(
		(question) => !validateQuestionCompletion(question, answers),
	);

	return {
		allCompleted: incompleteQuestions.length === 0,
		completedCount: questions.length - incompleteQuestions.length,
		incompleteQuestions,
	};
}
